import { useEffect, useSyncExternalStore } from "react";
import { useIsomorphicLayoutEffect } from "react-use";

// Whether the screen's large title is on screen: true, false, or null when
// the screen has none. The bar shows its own title only once it scrolls away.
// Kept on window with an event, not in module state: the shell's header and
// the page can load separate copies of this module.
const EVENT = "apollo:large-title";
type Store = Window & { __apolloLargeTitle?: boolean | null };

const read = () =>
  typeof window === "undefined"
    ? null
    : ((window as Store).__apolloLargeTitle ?? null);
const set = (next: boolean | null) => {
  if (next === read()) return;
  (window as Store).__apolloLargeTitle = next;
  window.dispatchEvent(new Event(EVENT));
};
const subscribe = (listener: () => void) => {
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
};

/** For a tab root or a pushed page's large title. */
export const useLargeTitle = (ref: React.RefObject<HTMLElement>) => {
  // Before paint, so a bar never shows its title for a frame and fades it out.
  useIsomorphicLayoutEffect(() => {
    if (!ref.current) return;
    set(true);
    return () => set(null);
  }, [ref]);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) =>
      set(entry.isIntersecting)
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
};

/** True while a large title is on screen, so a bar can hide its own. */
export const useLargeTitleVisible = () =>
  useSyncExternalStore(
    subscribe,
    () => read() === true,
    () => false
  );
